import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp, 
  Timestamp 
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth } from '../firebase';
import { PresentationOutline } from './SlidePlanService';

// Define FileWithContent interface directly to avoid circular dependency
export interface FileWithContent {
  id: string;
  name: string;
  type: string;
  size: number;
  content: string;
  base64Data?: string;
  summary?: string;
  storageURL?: string;
  tokenCount?: number;
}

// Define the presentation interface
export interface Presentation {
  id?: string;
  title: string;
  content: string;
  html: string;
  userId: string;
  contextFiles?: FileWithContent[];
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  slidePlan?: PresentationOutline;
}

class PresentationService {
  // Storage reference
  private storage = getStorage();

  /**
   * Create a new presentation
   */
  async createPresentation(presentation: Omit<Presentation, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const presentationData = {
        ...presentation,
        userId: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'presentations'), presentationData);
      console.log('Presentation created with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error creating presentation:', error);
      throw error;
    }
  }

  /**
   * Get all presentations for the current user
   */
  async getUserPresentations(): Promise<Presentation[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const q = query(
        collection(db, 'presentations'), 
        where('userId', '==', user.uid),
        orderBy('updatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const presentations: Presentation[] = [];
      
      querySnapshot.forEach((doc) => {
        presentations.push({
          id: doc.id,
          ...doc.data()
        } as Presentation);
      });
      
      return presentations;
    } catch (error) {
      console.error('Error getting user presentations:', error);
      throw error;
    }
  }

  /**
   * Get a specific presentation by ID
   */
  async getPresentation(id: string): Promise<Presentation | null> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const docRef = doc(db, 'presentations', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data() as Omit<Presentation, 'id'>;
        
        // Check if the presentation belongs to the current user
        if (data.userId !== user.uid) {
          throw new Error('Unauthorized access to presentation');
        }
        
        return {
          id: docSnap.id,
          ...data
        } as Presentation;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting presentation:', error);
      throw error;
    }
  }

  /**
   * Update an existing presentation
   */
  async updatePresentation(id: string, presentation: Partial<Omit<Presentation, 'id' | 'userId' | 'createdAt' | 'updatedAt'>>): Promise<string> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      // First check if the user owns this presentation
      const presentationRef = doc(db, 'presentations', id);
      const presentationSnap = await getDoc(presentationRef);
      
      if (!presentationSnap.exists()) {
        throw new Error('Presentation not found');
      }
      
      const presentationData = presentationSnap.data();
      if (presentationData.userId !== user.uid) {
        throw new Error('Unauthorized to update this presentation');
      }

      const updateData = {
        ...presentation,
        updatedAt: serverTimestamp()
      };

      await updateDoc(presentationRef, updateData);
      console.log('Presentation updated:', id);
      return id;
    } catch (error) {
      console.error('Error updating presentation:', error);
      throw error;
    }
  }

  /**
   * Delete a presentation
   */
  async deletePresentation(id: string): Promise<boolean> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      // First check if the user owns this presentation
      const presentationRef = doc(db, 'presentations', id);
      const presentationSnap = await getDoc(presentationRef);
      
      if (!presentationSnap.exists()) {
        throw new Error('Presentation not found');
      }
      
      const presentationData = presentationSnap.data();
      if (presentationData.userId !== user.uid) {
        throw new Error('Unauthorized to delete this presentation');
      }

      await deleteDoc(presentationRef);
      console.log('Presentation deleted:', id);
      return true;
    } catch (error) {
      console.error('Error deleting presentation:', error);
      throw error;
    }
  }

  /**
   * Upload a file to Firebase Storage and return the FileWithContent object
   */
  async uploadContextFile(file: File): Promise<FileWithContent> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Create a unique file path
      const fileId = `${Date.now()}-${file.name}`;
      const fileRef = ref(this.storage, `users/${user.uid}/context-files/${fileId}`);
      
      // Upload file to Storage
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);
      
      // For PDFs, just store reference, for text files extract content
      let content = '';
      let base64Data = undefined;
      
      if (file.type === 'application/pdf') {
        // For PDF, read as base64 for immediate use with Claude
        const base64 = await this.readFileAsBase64(file);
        base64Data = base64;
        content = `PDF Document (${Math.round(file.size / 1024)} KB)`;
      } else {
        // For text files, extract content
        content = await this.readFileAsText(file);
      }
      
      // Generate a summary for preview
      let summary = "";
      if (file.type !== 'application/pdf') {
        summary = content.substring(0, 150) + (content.length > 150 ? '...' : '');
      } else {
        summary = `PDF Document (${Math.round(file.size / 1024)} KB)`;
      }
      
      return {
        id: fileId,
        name: file.name,
        type: file.type,
        size: file.size,
        content,
        base64Data,
        summary,
        storageURL: downloadURL
      };
    } catch (error) {
      console.error('Error uploading context file:', error);
      throw error;
    }
  }
  
  /**
   * Read a file as Base64 encoded string
   */
  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = () => {
        // Extract the base64 data part from the data URL
        const result = reader.result as string;
        const base64 = result.split(',')[1]; // Remove the data:application/pdf;base64, prefix
        resolve(base64);
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file as Base64'));
      };
    });
  }
  
  /**
   * Read a file as text
   */
  private readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsText(file);
      
      reader.onload = () => {
        resolve(reader.result as string);
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file as text'));
      };
    });
  }

  /**
   * Update a presentation's slide plan
   */
  async updateSlidePlan(id: string, slidePlan: PresentationOutline): Promise<string> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const presentationRef = doc(db, 'presentations', id);
      const presentationSnapshot = await getDoc(presentationRef);
      
      if (!presentationSnapshot.exists()) {
        throw new Error('Presentation not found');
      }
      
      const presentationData = presentationSnapshot.data();
      if (presentationData.userId !== user.uid) {
        throw new Error('You do not have permission to update this presentation');
      }
      
      await updateDoc(presentationRef, {
        slidePlan,
        updatedAt: serverTimestamp()
      });
      
      return id;
    } catch (error) {
      console.error('Error updating slide plan:', error);
      throw error;
    }
  }

  /**
   * Get a presentation's slide plan
   */
  async getSlidePlan(id: string): Promise<PresentationOutline | null> {
    try {
      const presentationRef = doc(db, 'presentations', id);
      const presentationSnapshot = await getDoc(presentationRef);
      
      if (!presentationSnapshot.exists()) {
        return null;
      }
      
      const presentationData = presentationSnapshot.data();
      return presentationData.slidePlan || null;
    } catch (error) {
      console.error('Error getting slide plan:', error);
      return null;
    }
  }
}

const presentationService = new PresentationService();
export default presentationService; 